import { Base } from './base.model';

export interface Entity extends Base {
  name: string;
  type: string;
  /**
   * NEW / LOCAL (no table) → PENDING (finalised, its table waiting in the build queue) → READY (table
   * built) → SYNCED (tracked by Hasura). ERROR carries `statusReason`. Only the platform writes
   * PENDING, READY and ERROR (functions ADR 0005, glossary: Pending, Ready).
   */
  status: string;
  /** Why the table build failed, in the database's words — set with status ERROR, cleared otherwise. */
  statusReason?: string;
  data: TypeData[];
  updatedBySystem?: boolean;
  // System/control entity kept out of the builder's face: still provisioned + synced + usable by the
  // identity blocks, but filtered from the entity-management list AND the OOTB import picker. Used for
  // systemOotbTenant / systemOotbTenantInvite (systemOotbUser stays visible). Does NOT affect sync.
  hidden?: boolean;
  moduleSource?: string; // Name of module this entity was imported from
  description?: string; // Human-readable description — stored as Postgres COMMENT ON TABLE, feeds AI schema
  // LOCAL entities only: predefined seed rows (keyed by field name). At runtime, entity-list variables
  // backed by this entity auto-fill their empty .data with a copy of these — replacing a defaultFunction
  // push-seed for dropdown/option lists. Ignored for synced entities (they load from the DB).
  predefinedData?: { [fieldName: string]: any }[];
  /**
   * ATLAS COLUMN IDS — monotonic counter for `TypeData.atlasColumnId` (2026-07-29).
   *
   * Highest number handed out so far; the next mint is `seq + 1`. It exists so a number is NEVER
   * reused: counting the fields instead would shrink when one is deleted, and the next field would
   * inherit a dead column's id — silently re-pointing any saved function / benchmark / cached payload
   * that stored it. Same pair the Atlas map already keeps (`page.atlasSeq`, `atlasMeta.seq`).
   *
   * Per-entity, not project-wide: a column id only has to be unique WITHIN its entity, because the
   * entity is always named alongside it (`AtlasContext.entities[].queryColumns`). See
   * docs/plans/atlas-entity-column-ids.md.
   */
  columnSeq?: number;
  /**
   * ATLAS ENTITY HANDLE — "$E_1" (2026-07-29). What Forge emits where a step names an entity
   * (`QUERY.entity`, `CREATE_ENTITY.entity`), and the join target for `of` / `holdsEntity` /
   * `queryColumns[].refEntity`.
   *
   * Why not the Firestore doc id: `tasks_NzMxOT` is not a handle — it is a name with a provisioning
   * suffix, so the model is typing a real string and can misspell it, which is what tokenizing exists to
   * prevent. Why not the page-minted `$AC242`: that is re-minted per page, so the same entity has a
   * different id on every page.
   *
   * Minted ONCE from `AtlasGlobalMeta.entitySeq` and pinned for life. The underscore is load-bearing —
   * `$E1` collides with page code "E". See docs/plans/atlas-entity-column-ids.md.
   */
  atlasEntityId?: string;
  // Note: Role permissions are now managed separately via RoleService
  // and stored in the role documents, not in the entity
}

export interface TypeData {
  name: string;
  type: string;
  isUnique: boolean;
  primaryKey: boolean;
  startIncrementFrom: number;
  referenceEntityName: string;
  referenceEntityColumnName: string;
  multiple?: boolean;
  extendedAttributes?: boolean;
  id: string;
  data: TypeData[];
  defaultValue: any;
  /** TIMESTAMP fields only (2026-09-16): how the default is produced — 'now' (fresh per record) | 'fixed'
   *  (the ISO in defaultValue) | 'none' (null). Absent is read as 'now'. See field-default.util.ts. */
  defaultMode?: 'now' | 'fixed' | 'none';
  description?: string; // Human-readable description — stored as Postgres column comment, feeds AI schema
  /**
   * ATLAS COLUMN HANDLE — "$Q1_1", "$Q1_2" … = entity 1, column 1 (2026-07-29). What Forge emits in a
   * query's `conditions[].field` / `orderBy.field`; `resolveTokens` swaps in the real column name.
   *
   * The entity number is carried so a composed path shows which entity each hop lands in: a bare `Q_n`
   * renders `leadRefference.leadStage` as `Q_7.Q_7` (each is the 7th column of its own entity), where
   * `$Q2_7.$Q1_7` is legible and globally unique. `$Q1_7` cannot be confused with page "Q" variable 1
   * (`$Q1`) — `resolveTokens` anchors on the closing quote.
   *
   * EVERY field gets one, system fields included. Minting is identity, not offering.
   *
   * Why an id at all: `leadRefference` misspelled as the correct English `leadReference` yields a query
   * that compiles, runs, and silently returns nothing — and the validator explicitly SKIPS dotted fields
   * today (forge.validator.ts:277). An id makes a hallucinated column structurally impossible.
   *
   * Why it is NOT `TypeData.id`: that is empty for exactly the 111 system fields (`id`,
   * `id_auto_increment`, `parent_id`, AAOB system keys) measured across 29 entities, and empties are not
   * unique among siblings. A minted seq is unique by construction.
   *
   * Why it is NOT a variant's handle: one field has N variants (N routes through N holders) but is
   * exactly ONE column, and the two resolve to different strings — `$B3` → the full traversal token,
   * `Q_2` → the bare column name. Different slots, different jobs.
   *
   * Stamped at entity save (design time), persisted, never recomputed. Deep columns are COMPOSED from
   * these at grouped-view build time (`Q_12.Q_2` = leadRefference.leadStage) — never stored, so no
   * entity holds a closure and cycles are impossible. See docs/plans/atlas-entity-column-ids.md.
   */
  atlasColumnId?: string;
}

/**
 * Pre-defined audit entity schema to be seeded per client.
 * The resulting Hasura table will be `<clientId>_audit`.
 */
export const AUDIT_ENTITY: Entity = {
  id: 'audit',
  name: 'audit',
  type: 'TABLE',
  status: 'READY',
  // Hidden for now — audit is not fully implemented yet. Still seeded + synced (so the table exists
  // when we come back to it), just kept out of the builder's entity list + picker. Un-hide to resume.
  hidden: true,
  data: [
    {
      name: 'id',
      type: 'UUID',
      isUnique: true,
      primaryKey: true,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: '',
      data: [],
      defaultValue: ''
    },
    {
      name: 'entity_name',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'entity_name_audit',
      data: [],
      defaultValue: ''
    },
    {
      name: 'record_id',
      type: 'UUID',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'record_id_audit',
      data: [],
      defaultValue: ''
    },
    {
      name: 'operation',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'operation_audit',
      data: [],
      defaultValue: ''
    },
    {
      name: 'changed_by',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'changed_by_audit',
      data: [],
      defaultValue: ''
    },
    {
      name: 'changed_at',
      type: 'TIMESTAMP',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'changed_at_audit',
      data: [],
      defaultValue: ''
    },
    {
      name: 'snapshot',
      type: 'JSONB',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'snapshot_audit',
      data: [],
      defaultValue: ''
    }
  ]
};

/**
 * Local "role" entity seeded per project. NOT an OOTB entity and NOT a synced table — it's a LOCAL
 * display container that `get_roles_list` fills from zavoConfig (via the manageRole CF). Seeded like
 * AUDIT_ENTITY (a plain constant), so it never appears in the OOTB import picker.
 */
export const ROLE_LOCAL_ENTITY: Entity = {
  id: 'role_global',
  name: 'role',
  type: 'TABLE',
  status: 'LOCAL',
  updatedBySystem: true,
  data: [
    {
      name: 'name',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: 'userName_NDIyMj',
      data: [],
      defaultValue: ''
    }
  ]
};
